<template>
  <v-container
    id="planes-view"
    fluid
    tag="section"
  >
    <cumulus-edit-dialog
      ref="editPlaneShare"
      v-model="dialogPlaneShare"
      icon="$share"
      :title="$t('plane_share_informations')"
      @save="savePlaneShare"
      @escape="closeDialogPlaneShare"
    >
      <template v-slot:editform>
        <edit-plane-share
          :plane="editedItem"
        />
      </template>
    </cumulus-edit-dialog>

    <material-card
      icon="mdi-airplane"
      icon-small
      color="accent"
      :title="page_title"
      help="planes"
    >
      <v-card-title>
        <v-btn
          v-show="editor"
          v-shortkey.once="['f2']"
          color="primary"
          class="mb-2 ml-2"
          @shortkey="edit(-1)"
          @click="edit(-1)"
        >
          {{ $t('new_item',{ item: $t('planes') }) }}
        </v-btn>
        <searcher
          type="plane"
          @selected="selectPlane"
        />
        <plane-stat />
        <export-btn
          source-type="planes"
        />
        <export-btn
          source-type="planes"
          export-type="pdf"
        />
        <v-row class="ma-0">
          <v-spacer v-if="!$vuetify.breakpoint.mdAndDown" />
          <v-switch
            v-model="show_inactive"
            class="mx-2"
            :label="$t('show_inactive')"
            hide-details
          />
          <v-switch
            v-model="show_shared"
            class="mx-2"
            :label="$t('show_shared')"
            hide-details
          />
          <v-text-field
            v-model="search"
            append-icon="$search"
            class="ml-auto"
            hide-details
            :label="$t('search_records')"
            single-line
          />
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="computed_headers"
          :items="items"
          :loading="loading"
          :loading-text="$t('loading')"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="footerProps"
          :item-class="row_classes"
        >
          <template v-slot:item.avatar="{item}">
            <v-img
              v-if="item.avatar"
              max-height="50px"
              :src="'data:image/jpeg;base64,' + item.avatar.thumbnail"
            />
          </template>
          <template v-slot:item.reg_num="{item}">
            <v-row>
              {{ item.reg_num }}
              <p v-if="item.tail_number">
                ({{ item.tail_number }})
              </p>
            </v-row>
          </template>
          <template #item.actions="{item}">
            <plane-informations
              :plane="getPlaneInformations(item)"
            />
            <tooltip-btn
              v-if="technical_operator && item.is_owner && checkModule('maintenance') && item.maintenance_module_on"
              :color="item.components.length > 0 ? 'primary' : 'empty'"
              icon="$components"
              tooltip="tooltip_components"
              @click="components(item.id)"
            >
              <v-icon v-text="'$components'" />
            </tooltip-btn>
            <question-btn
              v-if="item.is_owner !== 1"
              color="delete"
              icon="$close"
              tooltip="tooltip_close_share"
              question="tooltip_close_share_question"
              question-description="tooltip_close_share_description"
              @click="clickClosePlaneShare(item.id)"
            />
            <tooltip-btn
              v-for="(action, i) in actions"
              :key="i"
              :show="!action.disable && (item.is_owner === 1 || (item.owner[0].id === 100 && action.icon === '$edit' ))"
              :color="action.color"
              :icon="action.icon"
              :tooltip="action.tooltip"
              :to="action.to ? action.to+item.id : ''"
              @click="action.func(item)"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, sync } from 'vuex-pathify'
  import ExportBtn from '@/components/ExportBtn'
  import PlaneInformations from '@/layouts/default/infos/PlaneInfo'
  import PlaneStat from '@/layouts/default/infos/PlaneStat'
  import EditPlaneShare from '@/layouts/default/edit/PlaneShare'
  import Searcher from '@/layouts/default/infos/Searcher'

  export default {
    name: 'Planes',
    components: {
      Searcher,
      EditPlaneShare,
      PlaneStat,
      PlaneInformations,
      ExportBtn,
    },
    data () {
      return {
        items: [],
        footerProps: {
          itemsPerPageOptions: [10, 20, 25, 50, 100],
        },
        total: 0,
        timer: null,
        show_inactive: false,
        show_shared: false,
        dialogPlaneShare: false,
      }
    },
    computed: {
      loading: get('app/loading'),
      profile: get('auth/profile'),
      options: sync('plane/options'),
      search: sync('plane/search'),
      competitionModuleOn () { return this.profile.organization.modules.filter(m => m.name === 'competition').length > 0 },
      headers () {
        return [
          {
            align: 'start',
            value: 'avatar',
            sortable: false,
            width: 50,
          },
          {
            text: this.$t('fields.reg_num'),
            value: 'reg_num',
            sortable: false,
          },
          {
            text: this.$t('fields.model'),
            value: 'model',
            sortable: false,
          },
          {
            hide_small: true,
            text: this.$t('fields.construction_date'),
            value: 'construction_date',
            sortable: false,
          },
          {
            hide_small: true,
            text: this.$t('fields.serial_no'),
            value: 'serial_no',
            sortable: false,
          },
          {
            text: this.$t('actions'),
            value: 'actions',
            sortable: false,
            align: 'right',
          },
        ]
      },
      actions () {
        return [
          { color: 'orange', icon: '$share', tooltip: 'tooltip_share', func: this.showDialogPlaneShare, disable: !this.editor },
          { color: 'blue', icon: '$documents', tooltip: 'tooltip_documents', to: '/documents/plane/' },
          // { color: 'blue', icon: '$components', tooltip: 'tooltip_components', func: this.components, disable: !this.technical_operator },
          { color: 'purple', icon: '$fuel', tooltip: 'tooltip_fuel_operations', to: '/fuel-operations/plane/', disable: !this.checkModule('fuel-management') },
          { color: 'edit', icon: '$edit', tooltip: 'tooltip_edit', to: '/plane/', disable: !this.editor },
        ]
      },
      page_title () { return this.$t('planes') },
      editor () { return this.profile.can['plane-edit'] },
      technical_operator () { return this.profile.can['component-list'] },
      computed_headers () { return this.headers.filter(h => h.hide_small !== this.$vuetify.breakpoint.mdAndDown) },
    },
    watch: {
      search: {
        handler () {
          if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
          }
          this.timer = setTimeout(() => {
            this.options.page = 1
            this.load()
          }, 800)
        },
        deep: true,
      },
      options: {
        handler () {
          this.load()
        },
        deep: true,
      },
      show_inactive: {
        handler () {
          this.load()
        },
        deep: true,
      },
      show_shared: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    mounted () {
      this.show_shared = this.competitionModuleOn
      // this.load()
    },
    methods: {
      getList: call('plane/search'),
      checkPlaneLimit: call('organization/checkPlaneLimit'),
      closePlaneShare: call('plane/unshare'),
      setOwner: call('plane/setOwner'),
      async load () {
        try {
          const { sortBy, sortDesc, page, itemsPerPage } = this.options
          await this.getList({
            page: page,
            per_page: itemsPerPage,
            sort: {
              field: sortBy,
              order: sortDesc,
            },
            search: this.search,
            show_inactive: this.show_inactive,
            show_shared: this.show_shared,
          }).then(response => {
            this.items = response.data
            this.total = response.total
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
        }
      },
      edit (id) {
        this.checkPlaneLimit().then(response => {
          this.$router.push(`/plane/${id}`).catch(() => {})
        })
      },
      components (id) {
        this.$router.push(`/plane/${id}/components/`).catch(() => {})
      },
      row_classes (item) {
        if (!item.functional) {
          return 'red lighten-5'
        } else if (!item.is_owner) {
          return 'grey lighten-3'
        }
      },
      showDialogPlaneShare (item) {
        this.editedItem = item
        this.dialogPlaneShare = true
      },
      closeDialogPlaneShare () {
        this.dialogPlaneShare = false
        this.load()
      },
      async savePlaneShare () {
        // await this.setMembership(this.editedItem).then(response => {
        this.closeDialogPlaneShare()
        // }).catch(e => {
        //   this.$refs.editMembership.setErrors(e)
        // })
      },
      clickClosePlaneShare (id) {
        this.closePlaneShare({
          plane_id: id,
        }).then(response => {
          this.load()
        })
      },
      checkModule (payload) {
        return this.profile.organization.modules.filter(m => m.name === payload).length > 0
      },
      getPlaneInformations (item) {
        // const result = this.getTotalTime(item)
        // console.log(item.reg_num)
        return item
      },
      async selectPlane (result) {
        await this.setOwner(
          {
            organization_id: this.profile.organization.id,
            organization_type: 1,
            plane_id: result.id,
            start_date: null,
            end_date: null,
            is_owner: true,
            is_valid: true,
          },
        ).then(response => {
          this.show_shared ? this.load() : this.show_shared = true
        }).catch(e => {
        })
      },
    },
  }
</script>
